<script>
  import { beforeUpdate, onMount } from 'svelte'
  import { pages, getPageContent } from '../../models/pages.js'

  export var params

  let page = null
  let blogs = []
  let urlPath = null
  let loading = false

  onMount(async () => {
    document.title = 'Puzzle Blog | Missing Piece Puzzle Exchange'
    blogs = (await pages.loadBlogs()) || []
  })

  beforeUpdate(async () => {
    if (!params.id) {
      if (page !== null) {
        page = null
        urlPath = null
        document.title = 'Puzzle Blog | Missing Piece Puzzle Exchange'
      }
      return
    }
    let currPath = `blog/${params.id}`
    if (urlPath != currPath) {
      urlPath = currPath
      loading = true
      page = await getPageContent(urlPath)
      loading = false
      if (page) {
        document.title = `${page.name} | Missing Piece Puzzle Exchange`
      }
    }
  })

  $: activePosts = blogs.filter((p) => p.active === true && p.type === 'blog')
  $: currentIndex = params.id
    ? activePosts.findIndex((p) => p.url_path === `blog/${params.id}`)
    : -1
  $: prevPost = currentIndex > 0 ? activePosts[currentIndex - 1] : null
  $: nextPost = currentIndex >= 0 && currentIndex < activePosts.length - 1 ? activePosts[currentIndex + 1] : null
</script>

<style type="text/less">
  .container {
    max-width: 740px;
    margin: 2rem auto;
    padding: 0 var(--spacing);
  }

  /* Index view */
  .blog-index h1 {
    margin-bottom: var(--spacing-sm);
  }

  .blog-list {
    list-style: none;
    padding: 0;
    margin: var(--spacing) 0 0;
  }

  .blog-list-item a {
    display: block;
    padding: var(--spacing) 0;
    border-top: solid 1px var(--col-gray-90);

    &:link, &:visited {
      text-decoration: none;
      color: var(--col-text);
    }

    &:hover h2, &:focus h2 {
      color: var(--col-accent-2);
    }

    h2 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      transition: color 0.15s;
    }

    span {
      font-size: 0.85rem;
      color: var(--col-gray-50);
      margin-top: 0.25rem;
      display: block;
    }
  }

  .blog-list-item:last-child a {
    border-bottom: solid 1px var(--col-gray-90);
  }

  /* Post view */
  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.9rem;
    margin-bottom: var(--spacing);

    &:link, &:visited {
      text-decoration: none;
      color: var(--col-gray-40);
    }

    &:hover, &:focus {
      text-decoration: underline;
      color: var(--col-text);
    }
  }

  article h1 {
    margin-top: 0;
  }

  .post-nav {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: var(--spacing-lg);
    padding-top: var(--spacing);
    border-top: solid 1px var(--col-gray-90);
    gap: var(--spacing);

    a {
      font-size: 0.9rem;
      max-width: 45%;

      &:link, &:visited {
        text-decoration: none;
        color: var(--col-gray-40);
      }

      &:hover, &:focus {
        color: var(--col-text);
      }
    }

    .post-nav-label {
      display: block;
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: var(--col-gray-60);
      margin-bottom: 0.25rem;
    }

    .post-nav-next {
      text-align: right;
      margin-left: auto;
    }
  }

  .loading {
    text-align: center;
    padding: var(--spacing);
    color: var(--col-gray-50);
  }

  @media (max-width: 600px) {
    .blog-grid {
      grid-template-columns: 1fr;
    }

    .post-nav {
      flex-direction: column;
      gap: var(--spacing-sm);

      a {
        max-width: 100%;
      }

      .post-nav-next {
        text-align: left;
        margin-left: 0;
      }
    }
  }
</style>

<svelte:head>
  {#if page}
    <title>{page.name} | Missing Piece Puzzle Exchange</title>
    <meta name="description" content="Read {page.name} on the Missing Piece Puzzle Exchange blog — tips, tricks, and puzzle inspiration." />
    <meta property="og:title" content="{page.name} | Missing Piece Puzzle Exchange" />
    <meta property="og:type" content="article" />
    <meta property="og:url" content="https://www.puzzleexchange.com/blog/{params.id}" />
    <link rel="canonical" href="https://www.puzzleexchange.com/blog/{params.id}" />
  {:else}
    <title>Puzzle Blog | Missing Piece Puzzle Exchange</title>
    <meta name="description" content="Tips, tricks, and inspiration for jigsaw puzzle lovers. Read the Missing Piece Puzzle Exchange blog." />
    <meta property="og:title" content="Puzzle Blog | Missing Piece Puzzle Exchange" />
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://www.puzzleexchange.com/blog" />
    <link rel="canonical" href="https://www.puzzleexchange.com/blog" />
  {/if}
</svelte:head>

<div class="container">

  {#if !page && !loading}
    <!-- Blog index -->
    <div class="blog-index">
      <h1>Puzzle Exchange Blog</h1>
      <p>Tips, tricks, and inspiration for jigsaw puzzle lovers.</p>
      {#if activePosts.length > 0}
        <ul class="blog-list">
          {#each activePosts as post}
            <li class="blog-list-item">
              <a href={`/${post.url_path}`}>
                <h2>{post.name}</h2>
                <span>Read post →</span>
              </a>
            </li>
          {/each}
        </ul>
      {:else}
        <p>No posts yet — check back soon!</p>
      {/if}
    </div>

  {:else}
    <!-- Blog post -->
    <a class="back-link" href="/blog">← All posts</a>

    {#if loading}
      <p class="loading">Loading...</p>
    {:else if page}
      <article>
        <h1>{page.name}</h1>
        {@html page.html_content}
      </article>

      {#if prevPost || nextPost}
        <nav class="post-nav">
          {#if prevPost}
            <a href={`/${prevPost.url_path}`}>
              <span class="post-nav-label">← Previous</span>
              {prevPost.name}
            </a>
          {/if}
          {#if nextPost}
            <a class="post-nav-next" href={`/${nextPost.url_path}`}>
              <span class="post-nav-label">Next →</span>
              {nextPost.name}
            </a>
          {/if}
        </nav>
      {/if}
    {/if}
  {/if}

</div>
